<template>
  <div
    class="invisible fixed bottom-[-400px] left-4 z-50 transition-all duration-500 hover:bottom-4 md:bottom-[-300px]"
  >
    <div id="embed-iframe"></div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

import { spotifyStore } from '../../composables/useSpotify'

const breakpoints = useBreakpoints(breakpointsTailwind)

type PlaybackType = {
  data: {
    duration: number
    isBuffering: boolean
    isPaused: boolean
    position: number
  }
}

// Cleanup function to prevent memory leaks
const cleanupSpotify = () => {
  if (spotifyStore.embedController) {
    try {
      // Note: Spotify API may not have removeListener method
      // The cleanup is mainly about nullifying references
      spotifyStore.embedController = null
    } catch (error) {
      console.warn('Error cleaning up Spotify controller:', error)
    }
  }

  // Clean up global callback
  if (typeof window !== 'undefined') {
    // @ts-expect-error - Spotify API global callback
    window.onSpotifyIframeApiReady = null
  }

  // Reset store state
  spotifyStore.initialized = false
  spotifyStore.currentSeconds = 0
  spotifyStore.duration = 0
  spotifyStore.isPaused = true
  spotifyStore.isBuffering = false
  spotifyStore.currentPodcast = ''
  spotifyStore.added = false
  spotifyStore.location = ''
  spotifyStore.IFrameAPI = null
}

const initSpotifyIframe = () => {
  let element = document.getElementById('embed-iframe')

  let options = {
    uri: 'spotify:episode:7makk4oTQel546B0PZlDM5',
  }
  let callback = (EmbedController: typeof spotifyStore.embedController) => {
    spotifyStore.embedController = EmbedController
    EmbedController.addListener('ready', () => {
      EmbedController.iframeElement.title = 'Spotify Player' // Adds iframe title, re lighthouse warning
    })
    EmbedController.addListener('playback_update', (e: PlaybackType) => {
      spotifyStore.currentSeconds = Math.round(e.data.position / 1000)
      spotifyStore.duration = Math.round(e.data.duration / 1000)
      spotifyStore.isPaused = e.data.isPaused
      spotifyStore.isBuffering = e.data.isBuffering
    })
  }
  // @ts-expect-error - Spotify API method
  spotifyStore.IFrameAPI.createController(element, options, callback)
}

const addSpotify = () => {
  const desktop = breakpoints.greaterOrEqual('lg')
  const time = desktop.value ? 3000 : 4000

  const existingScript = document.querySelector(
    `script[src="https://open.spotify.com/embed-podcast/iframe-api/v1"]`,
  )

  if (existingScript) {
    initSpotifyIframe()
    return
  }
  // @ts-expect-error - Spotify API global callback
  window.onSpotifyIframeApiReady = (IFrameAPI: typeof spotifyStore.IFrameAPI) => {
    spotifyStore.IFrameAPI = IFrameAPI
    initSpotifyIframe()
  }

  setTimeout(() => {
    const script = document.createElement('script')
    script.src = 'https://open.spotify.com/embed-podcast/iframe-api/v1'
    script.async = true
    document.getElementsByTagName('body')[0].appendChild(script)
  }, time)
}

onMounted(() => {
  // Only load if there's a current podcast
  if (!spotifyStore.hasPodcastBlock) return
  addSpotify()
})

// Clean up on component unmount to prevent memory leaks
onUnmounted(() => {
  cleanupSpotify()
})
</script>
